import { observer } from 'mobx-react-lite'
import { Table, TableRows } from 'iconoir-react'
import { Button } from 'app/components/ui/button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { HIDE_TABLE_COLUMN_LINES_SETTING } from 'app/hooks/useTableColumnLines'
import { useUserSettingStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'

/**
 * Flips the vertical dividers between table body columns. The preference is
 * deliberately global rather than per table: it reads as a choice about how
 * tables should look, not about the one you happen to be standing on.
 *
 * The glyph shows the current state (ruled or plain) rather than the action, so
 * the button still says something once the toolbar label drops away on desktop.
 */
export const TableColumnLinesToggle = observer(function TableColumnLinesToggle(): React.ReactElement {
  const store = useUserSettingStore()
  // The key is absent until `useTableColumnLinesSync`'s read resolves, and absent
  // reads as "lines shown". Acting on that guess would let the first click save a
  // value the user never saw the button offer, so wait for the real one.
  const loaded = HIDE_TABLE_COLUMN_LINES_SETTING in store.settings
  const hidden = store.settings[HIDE_TABLE_COLUMN_LINES_SETTING] === `true`
  const label = hidden ? `Show column lines` : `Hide column lines`
  // Full grid when the vertical rules are on, rows-only when they are off, so the
  // glyph is a small picture of the table you are about to get.
  const Glyph = hidden ? TableRows : Table

  return (
    <TooltipWrapper message={label} placement="bottom">
      <Button
        variant="white"
        size="sm"
        aria-label={label}
        aria-pressed={hidden}
        disabled={!loaded}
        onClick={() => bg(async () => store.save(HIDE_TABLE_COLUMN_LINES_SETTING, JSON.stringify(!hidden)))}
      >
        <Glyph className="h-4 w-4" />
        <div className="text-ui-xs md:hidden">{label}</div>
      </Button>
    </TooltipWrapper>
  )
})
